import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const AgentCard = ({ agent, onView, onEdit, onShare, onClone, onMigrate, onCopyEmbed, onCopySupport, onPin, onAddToFolder, onDelete }) => {
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });

  const handleContextMenu = (e) => {
    e?.preventDefault();
    setContextMenuPosition({ x: e?.clientX, y: e?.clientY });
    setShowContextMenu(true);
  };

  const handleCloseContextMenu = () => {
    setShowContextMenu(false);
  };

  const contextMenuItems = [
    { label: 'Bearbeiten', icon: 'Edit', action: () => onEdit(agent?.id) },
    { label: 'Teilen', icon: 'Share', action: () => onShare(agent?.id) },
    { label: 'Klonen', icon: 'Copy', action: () => onClone(agent?.id) },
    { label: 'Migrieren', icon: 'ArrowRightLeft', action: () => onMigrate(agent?.id) },
    { label: 'Embed-Code kopieren', icon: 'Code', action: () => onCopyEmbed(agent?.id) },
    { label: 'Support-Info kopieren', icon: 'Info', action: () => onCopySupport(agent?.id) },
    { label: agent?.isPinned ? 'Von oben entfernen' : 'An Spitze anheften', icon: 'Pin', action: () => onPin(agent?.id) },
    { label: 'Zu Ordner hinzufügen', icon: 'FolderPlus', action: () => onAddToFolder(agent?.id) },
    { label: 'Löschen', icon: 'Trash2', action: () => onDelete(agent?.id), destructive: true }
  ];

  const formatNumber = (num) => {
    if (num >= 1000) {
      return (num / 1000)?.toFixed(1) + 'k';
    }
    return num?.toString();
  };

  const getModelColor = (model) => {
    const colors = {
      'GPT-4': 'bg-blue-100 text-blue-800',
      'GPT-3.5': 'bg-green-100 text-green-800',
      'Claude': 'bg-purple-100 text-purple-800',
      'Gemini': 'bg-orange-100 text-orange-800'
    };
    return colors?.[model] || 'bg-gray-100 text-gray-800';
  };

  return (
    <>
      <div 
        className="bg-card border border-border rounded-lg p-6 hover:shadow-md transition-all duration-200 relative group"
        onContextMenu={handleContextMenu}
      >
        {/* Pin indicator */}
        {agent?.isPinned && (
          <div className="absolute top-3 right-3">
            <Icon name="Pin" size={16} className="text-primary" />
          </div>
        )}

        {/* Agent Avatar */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 rounded-lg overflow-hidden bg-muted flex items-center justify-center">
              {agent?.avatar ? (
                <Image 
                  src={agent?.avatar} 
                  alt={`${agent?.name} Avatar`}
                  className="w-full h-full object-cover"
                />
              ) : (
                <Icon name="Bot" size={24} className="text-muted-foreground" />
              )}
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-foreground text-lg mb-1">{agent?.name}</h3>
              <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getModelColor(agent?.model)}`}>
                {agent?.model}
              </span>
            </div>
          </div>
          
          <Button
            variant="ghost"
            size="sm"
            iconName="MoreVertical"
            className="opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={handleContextMenu}
          />
        </div>

        {/* Tags */}
        {agent?.tags && agent?.tags?.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {agent?.tags?.slice(0, 3)?.map((tag, index) => (
              <span 
                key={index}
                className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-md"
              >
                {tag}
              </span>
            ))}
            {agent?.tags?.length > 3 && (
              <span className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-md">
                +{agent?.tags?.length - 3}
              </span>
            )}
          </div>
        )}

        {/* Statistics */}
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="text-center">
            <div className="text-lg font-semibold text-foreground">{formatNumber(agent?.chatCount)}</div>
            <div className="text-xs text-muted-foreground">Chats</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-semibold text-foreground">{formatNumber(agent?.messageCount)}</div>
            <div className="text-xs text-muted-foreground">Nachrichten</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-semibold text-foreground">{formatNumber(agent?.characterCount)}</div>
            <div className="text-xs text-muted-foreground">Zeichen</div>
          </div>
        </div>

        {/* Activity Status */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <div className={`w-2 h-2 rounded-full ${agent?.isActive ? 'bg-success' : 'bg-muted-foreground'}`} />
            <span className="text-sm text-muted-foreground">
              {agent?.isActive ? 'Aktiv' : 'Inaktiv'}
            </span>
          </div>
          <span className="text-xs text-muted-foreground">
            Erstellt: {new Date(agent.createdAt)?.toLocaleDateString('de-DE')}
          </span>
        </div>

        {/* Quick Actions */}
        <div className="flex space-x-2">
          <Button
            variant="outline"
            size="sm"
            iconName="Eye"
            iconPosition="left"
            onClick={() => onView(agent?.id)}
            className="flex-1"
          >
            Anzeigen
          </Button>
          <Button
            variant="default"
            size="sm"
            iconName="Edit"
            iconPosition="left"
            onClick={() => onEdit(agent?.id)}
            className="flex-1"
          >
            Bearbeiten
          </Button>
        </div>
      </div>
      {/* Context Menu */}
      {showContextMenu && (
        <>
          <div 
            className="fixed inset-0 z-40" 
            onClick={handleCloseContextMenu}
          />
          <div 
            className="fixed bg-popover border border-border rounded-md shadow-lg z-50 py-1 min-w-48 animate-scale-in"
            style={{
              left: `${contextMenuPosition?.x}px`,
              top: `${contextMenuPosition?.y}px`,
            }}
          >
            {contextMenuItems?.map((item, index) => (
              <button
                key={index}
                onClick={() => {
                  item?.action();
                  handleCloseContextMenu();
                }}
                className={`w-full px-3 py-2 text-left text-sm flex items-center space-x-2 hover:bg-muted transition-colors ${
                  item?.destructive ? 'text-destructive hover:text-destructive' : 'text-popover-foreground'
                }`}
              >
                <Icon name={item?.icon} size={16} />
                <span>{item?.label}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </>
  );
};

export default AgentCard;